import React from 'react';
import { Review } from '../types';
import { StarRating } from './StarRating';
import { Button } from './Button';
import { AlertCircle, CheckCircle, XCircle, Trash2 } from 'lucide-react';

interface AdminReviewCardProps {
  review: Review;
  onApprove: (reviewId: number) => void;
  onReject: (reviewId: number) => void;
  onDelete: (reviewId: number) => void;
  loading?: boolean;
}

export const AdminReviewCard: React.FC<AdminReviewCardProps> = ({
  review,
  onApprove,
  onReject,
  onDelete,
  loading = false,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = () => {
    if (review.isFlagged) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
          <AlertCircle className="w-3 h-3" />
          Flagged
        </span>
      );
    }
    if (review.status === 'approved') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
          <CheckCircle className="w-3 h-3" />
          Approved
        </span>
      );
    }
    if (review.status === 'rejected') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
          <XCircle className="w-3 h-3" />
          Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
        Pending
      </span>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
              {review.userName ? review.userName.charAt(0).toUpperCase() : 'U'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-gray-900">
                  {review.userName || 'Verified Service User'}
                </p>
                {getStatusBadge()}
              </div>
              <p className="text-sm text-gray-500">{formatDate(review.createdAt)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <StarRating rating={review.rating} size={18} />
            <span className="text-sm font-medium text-gray-700">
              {review.rating} star{review.rating !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
          {review.comment}
        </p>
      </div>

      {review.isFlagged && review.flagReason && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="text-sm text-orange-800">
            <span className="font-semibold">Flag Reason:</span> {review.flagReason}
          </p>
        </div>
      )}

      {review.rejectReason && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">
            <span className="font-semibold">Rejection Reason:</span> {review.rejectReason}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
        <div>
          <span className="text-gray-500">User ID:</span>
          <span className="ml-2 font-medium text-gray-900">{review.userId}</span>
        </div>
        <div>
          <span className="text-gray-500">Provider ID:</span>
          <span className="ml-2 font-medium text-gray-900">{review.providerId}</span>
        </div>
        <div>
          <span className="text-gray-500">Booking ID:</span>
          <span className="ml-2 font-medium text-gray-900">{review.bookingId}</span>
        </div>
        <div>
          <span className="text-gray-500">Review ID:</span>
          <span className="ml-2 font-medium text-gray-900">{review.reviewId}</span>
        </div>
      </div>

      {review.status !== 'approved' && review.status !== 'rejected' && (
        <div className="flex gap-2 pt-4 border-t border-gray-200">
          <Button
            variant="primary"
            onClick={() => onApprove(review.reviewId)}
            disabled={loading}
            className="flex-1"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Approve
          </Button>
          <Button
            variant="secondary"
            onClick={() => onReject(review.reviewId)}
            disabled={loading}
            className="flex-1"
          >
            <XCircle className="w-4 h-4 mr-1" />
            Reject
          </Button>
          <Button
            variant="secondary"
            onClick={() => onDelete(review.reviewId)}
            disabled={loading}
            className="px-4"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}

      {(review.status === 'approved' || review.status === 'rejected') && (
        <div className="flex gap-2 pt-4 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={() => onDelete(review.reviewId)}
            disabled={loading}
            className="flex-1"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete Review
          </Button>
        </div>
      )}
    </div>
  );
};
